import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { WishlistItemDto } from './dto/wishlist.dto';
import { WishlistService } from './services/wishlist.service';
import { addToWishlistUtility } from '../../shared/components/FuncationsCartAndWishlist/AddtoWishlistUtility';
import { useAuth } from '../Auth/AuthContext';
import { wishlistApiService } from '../../services/wishlist.api.service';

// Product interface for adding items to wishlist
export interface ProductDto {
  id: string;
  _id?: string;
  title: string;
  slug: string;
  description?: string;
  sku?: string;
  price?: number;
  compareAtPrice?: number | null;
  quantity?: number;
  weight?: number;
  weightUnit?: string;
  categories?: string[];
  variants?: any[];
  images?: any[];
  isActive?: boolean;
  isFeatured?: boolean;
  hasVariants?: boolean;
  requiresShipping?: boolean;
  taxable?: boolean;
  tags?: string[];
  trackInventory?: boolean;
  inventoryPolicy?: string;
  reviewCount?: number;
  ratingsQuantity?: number;
  meta?: { views?: number; purchases?: number };
  createdAt?: string;
  updatedAt?: string;
  __v?: number;
  rating?: number;
}

interface WishlistContextType {
  items: WishlistItemDto[];
  addItem: (product: ProductDto, variantSku?: string) => void;
  removeItem: (productId: string, variantSku?: string) => void;
  isItemInWishlist: (productId: string, variantSku?: string) => boolean;
  clearWishlist: () => void;
  navigateToProduct: (wishlistItem: WishlistItemDto) => void;
  loading: boolean;
  refreshWishlist: () => void;
}

const WishlistContext = createContext<WishlistContextType | undefined>(undefined);

export function WishlistProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<WishlistItemDto[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();
  const [isFetching, setIsFetching] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  // Fetch wishlist from backend for authenticated users
  const fetchWishlistFromBackend = useCallback(async () => {
    setIsFetching(true);
    setLoading(true);
    try {
      const response = await wishlistApiService.getWishlist();
      if (response.success && response.data) {
        // Transform backend data to match WishlistItemDto interface
        const transformedItems: WishlistItemDto[] = response.data.map((item: any) => ({
          id: `${item.product?.id || item.productId}-${item.variantSku || 'default'}`,
          _id: `${item.product?.id || item.productId}-${item.variantSku || 'default'}`,
          product: {
            id: item.product?.id || item.productId || item.product?._id,
            _id: item.product?.id || item.productId || item.product?._id,
            title: item.product?.title || '',
            slug: item.product?.slug || item.productId,
            description: item.product?.description || '',
            sku: item.variantSku || item.product?.sku || '',
            price: item.product?.price || 0,
            compareAtPrice: item.product?.compareAtPrice || null,
            quantity: item.product?.quantity || 0,
            weight: item.product?.weight || 0,
            weightUnit: item.product?.weightUnit || 'g',
            categories: item.product?.categories || [],
            variants: item.product?.variants || [],
            images: item.product?.images || [],
            imageUrl: item.product?.imageUrl || item.product?.image || '',
            isActive: item.product?.isActive ?? true,
            isFeatured: item.product?.isFeatured ?? false,
            hasVariants: item.product?.hasVariants ?? false,
            requiresShipping: item.product?.requiresShipping ?? false,
            taxable: item.product?.taxable ?? false,
            tags: item.product?.tags || [],
            trackInventory: item.product?.trackInventory ?? false,
            inventoryPolicy: item.product?.inventoryPolicy || 'deny',
            reviewCount: item.product?.reviewCount || 0,
            ratingsQuantity: item.product?.ratingsQuantity || 0,
            meta: item.product?.meta || { views: 0, purchases: 0 },
            createdAt: item.product?.createdAt || new Date().toISOString(),
            updatedAt: item.product?.updatedAt || new Date().toISOString(),
            __v: item.product?.__v || 0,
            defaultVariant: item.variantInfo || {
              name: '',
              sku: item.variantSku || '',
              price: item.product?.price || 0,
              quantity: item.product?.quantity || 0,
              weight: item.product?.weight || 0,
              weightUnit: item.product?.weightUnit || 'g',
              requiresShipping: item.product?.requiresShipping ?? false,
              taxable: item.product?.taxable ?? false,
              trackInventory: item.product?.trackInventory ?? false,
              isDefault: true,
            },
            rating: item.product?.rating || 0,
          },
          variantId: item.variantId || item.variantSku || '',
          variantSku: item.variantSku || '',
          variantInfo: item.variantInfo || null,
          productDetails: {},
        }));
        
        setItems(transformedItems);
        
        // Also save to localStorage for offline access
        if (typeof window !== 'undefined') {
          localStorage.setItem('wishlistItems', JSON.stringify(transformedItems));
        }
      }
    } catch (error) {
      setItems([]);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  }, []);
  const mergeWishlistOnLogin = useCallback(async () => {
    if (isFetching) return;
    
    setIsFetching(true);
    setLoading(true);
    try {
      const localWishlist = typeof window !== 'undefined' ? 
        JSON.parse(localStorage.getItem('wishlistItems') || '[]') : [];
      
      if (localWishlist.length === 0) {        await fetchWishlistFromBackend();
        return;
      }

      const guestItems = localWishlist.map((item: WishlistItemDto) => ({
        productId: item.product.id,
        variantSku: item.variantSku || '',
        variantId: item.variantId || item.variantSku || '',
        img: item.product.images?.[0]?.url || item.product.imageUrl || ''
      }));


      const mergeResponse = await wishlistApiService.mergeWishlist(guestItems);
      
      if (mergeResponse.success && mergeResponse.data) {
        const transformedItems: WishlistItemDto[] = mergeResponse.data.map((item: any) => ({
          id: `${item.product?.id || item.productId}-${item.variantSku || 'default'}`,
          _id: `${item.product?.id || item.productId}-${item.variantSku || 'default'}`,
          product: {
            id: item.product?.id || item.productId || item.product?._id,
            _id: item.product?.id || item.productId || item.product?._id,
            title: item.product?.title || '',
            slug: item.product?.slug || item.productId,
            description: item.product?.description || '',
            sku: item.variantSku || item.product?.sku || '',
            price: item.product?.price || 0,
            compareAtPrice: item.product?.compareAtPrice || null,
            quantity: item.product?.quantity || 0,
            weight: item.product?.weight || 0,
            weightUnit: item.product?.weightUnit || 'g',
            categories: item.product?.categories || [],
            variants: item.product?.variants || [],
            images: item.product?.images || [],
            imageUrl: item.product?.imageUrl || item.product?.image || '',
            isActive: item.product?.isActive ?? true,
            isFeatured: item.product?.isFeatured ?? false,
            hasVariants: item.product?.hasVariants ?? false,
            requiresShipping: item.product?.requiresShipping ?? false,
            taxable: item.product?.taxable ?? false,
            tags: item.product?.tags || [],
            trackInventory: item.product?.trackInventory ?? false,
            inventoryPolicy: item.product?.inventoryPolicy || 'deny',
            reviewCount: item.product?.reviewCount || 0,
            ratingsQuantity: item.product?.ratingsQuantity || 0,
            meta: item.product?.meta || { views: 0, purchases: 0 },
            createdAt: item.product?.createdAt || new Date().toISOString(),
            updatedAt: item.product?.updatedAt || new Date().toISOString(),
            __v: item.product?.__v || 0,
            defaultVariant: item.variantInfo || {
              name: '',
              sku: item.variantSku || '',
              price: item.product?.price || 0,
              quantity: item.product?.quantity || 0,
              weight: item.product?.weight || 0,
              weightUnit: item.product?.weightUnit || 'g',
              requiresShipping: item.product?.requiresShipping ?? false,
              taxable: item.product?.taxable ?? false,
              trackInventory: item.product?.trackInventory ?? false,
              isDefault: true,
            },
            rating: item.product?.rating || 0,
          },
          variantId: item.variantId || item.variantSku || '',
          variantSku: item.variantSku || '',
          variantInfo: item.variantInfo || null,
          productDetails: {},
        }));
        
        setItems(transformedItems);
        
        // Also save to localStorage for offline access
        if (typeof window !== 'undefined') {
          localStorage.setItem('wishlistItems', JSON.stringify(transformedItems));
        }
        
        if (typeof window !== 'undefined') {
          localStorage.removeItem('wishlistItems');
        }
      } else {
        await fetchWishlistFromBackend();
      }
      
    } catch (error) {
      await fetchWishlistFromBackend();
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  }, [fetchWishlistFromBackend]);

  const fetchWishlistFromLocalStorage = useCallback(() => {
    try {
      const wishlistItems = WishlistService.getWishlist();
      setItems(Array.isArray(wishlistItems) ? wishlistItems : []);
    } catch (err) {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    if (isLoading) return;
    
    if (!isAuthenticated) {
      setItems([]);
      WishlistService.clearWishlist();
      setHasInitialized(false);
    } else if (isAuthenticated && !hasInitialized) {
      setHasInitialized(true);
      mergeWishlistOnLogin();
    }
  }, [isAuthenticated, isLoading, mergeWishlistOnLogin]); // Removed hasInitialized and fetchWishlistFromBackend from dependencies

  useEffect(() => {
    if (!isAuthenticated && !isLoading && !hasInitialized) {
      setHasInitialized(true);
      fetchWishlistFromLocalStorage();
    }
  }, [isAuthenticated, isLoading, hasInitialized, fetchWishlistFromLocalStorage]);

  // Refresh function that can be called manually
  const refreshWishlist = useCallback(() => {
    if (isAuthenticated) {
      fetchWishlistFromBackend();
    } else {
      fetchWishlistFromLocalStorage();
    }
  }, [isAuthenticated, fetchWishlistFromBackend, fetchWishlistFromLocalStorage]);

  const addItem = (product: ProductDto, variantSku?: string) => {
    if (!product.id) {
      return;
    }

    const variantId = (product as any).variantId || undefined;

    if (isItemInWishlist(product.id, variantSku)) {
      return;
    }

    setLoading(true);

    // If user is authenticated, add to backend
    if (isAuthenticated) {
      const payload = {
        productId: product.id,
        variantSku: variantSku || '',
        variantId: variantId || variantSku || '',
        img: product.images?.[0]?.url || product.images?.[0] || ''
      };
      wishlistApiService.addToWishlist(payload)
        .then(response => {
          if (response.success) {
            fetchWishlistFromBackend().then(() => {
              // Item added to wishlist silently
            });
          } else {
            // Failed to add item
          }
        })
        .catch(() => {
          // Failed to add item to wishlist
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // Guest user - store in localStorage
      try {
        const newItem: WishlistItemDto = {
          id: `${product.id}-${variantSku || 'default'}`,
          _id: `${product.id}-${variantSku || 'default'}`,
          product: {
            id: product.id,
            _id: product.id,
            title: product.title || '',
            slug: product.slug || product.id,
            description: product.description || '',
            sku: variantSku || '',
            price: product.price || 0,
            compareAtPrice: product.compareAtPrice || null,
            quantity: product.quantity || 0,
            weight: product.weight || 0,
            weightUnit: product.weightUnit || 'g',
            categories: product.categories || [],
            variants: product.variants || [],
            images: product.images || [],
            isActive: product.isActive || true,
            isFeatured: product.isFeatured || false,
            hasVariants: product.hasVariants || false,
            requiresShipping: product.requiresShipping || false,
            taxable: product.taxable || false,
            tags: product.tags || [],
            trackInventory: product.trackInventory || false,
            inventoryPolicy: product.inventoryPolicy || 'deny',
            reviewCount: product.reviewCount || 0,
            ratingsQuantity: product.ratingsQuantity || 0,
            meta: product.meta || { views: 0, purchases: 0 },
            createdAt: product.createdAt || new Date().toISOString(),
            updatedAt: product.updatedAt || new Date().toISOString(),
            __v: product.__v || 0,
            defaultVariant: {
              name: '',
              sku: variantSku || '',
              price: product.price || 0,
              quantity: product.quantity || 0,
              weight: product.weight || 0,
              weightUnit: product.weightUnit || 'g',
              requiresShipping: product.requiresShipping || false,
              taxable: product.taxable || false,
              trackInventory: product.trackInventory || false,
              isDefault: true,
            },
            rating: product.rating || 0,
            imageUrl: undefined
          },
          variantId: variantSku || '',
          variantSku: variantSku || '',
          variantInfo: null,
          productDetails: {},
        };

        const updatedItems = addToWishlistUtility(items, newItem);
        WishlistService.addItem(newItem);
        setItems(updatedItems);
      } catch (error) {
        // Error adding item to wishlist
      } finally {
        setLoading(false);
      }
    }
  };

  const removeItem = (productId: string, variantSku: string = '') => {
    setLoading(true);
    
    // If user is authenticated, remove from backend
    if (isAuthenticated) {
      wishlistApiService.removeFromWishlist(productId, variantSku)
        .then(response => {
          if (response.success) {
            // Refresh wishlist from backend
            fetchWishlistFromBackend().then(() => {
              // Item removed silently
            });
          } else {
            // Failed to remove item
          }
        })
        .catch(() => {
          // Failed to remove item from wishlist
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // Guest user - remove from localStorage
      try {
        const updatedItems = WishlistService.removeItem(productId, variantSku);
        setItems(updatedItems);
      } catch (error) {
        // Error removing item
      } finally {
        setLoading(false);
      }
    }
  };

  const isItemInWishlist = (productId: string, variantSku?: string) => {
    return items.some(item => 
      item.product.id === productId && 
      (!variantSku || item.variantSku === variantSku)
    );
  };

  const clearWishlist = () => {
    setLoading(true);
    
    // If user is authenticated, clear in backend
    if (isAuthenticated) {
      wishlistApiService.clearWishlist()
        .then(response => {
          if (response.success) {
            setItems([]);
          } else {
            // Failed to clear wishlist
          }
        })
        .catch(() => {
          // Failed to clear wishlist
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      // Guest user - clear localStorage
      try {
        WishlistService.clearWishlist();
        setItems([]);
      } catch (error) {
        // Error clearing wishlist
      } finally {
        setLoading(false);
      }
    }
  };

  const getProductImages = (productData: any): any[] => {
    const images: any[] = [];
    
    if (productData.images && Array.isArray(productData.images) && productData.images.length > 0) {
      productData.images.forEach((img: any, index: number) => {
        if (typeof img === 'string') {
          images.push({
            url: img,
            altText: `${productData.title} - Image ${index + 1}`,
            isDefault: index === 0
          });
        } else if (img && img.url) {
          images.push({
            url: img.url,
            altText: img.altText || `${productData.title} - Image ${index + 1}`,
            isDefault: img.isDefault || index === 0
          });
        }
      });
    }
    
    if (productData.imageUrl && typeof productData.imageUrl === 'string') {
      images.push({
        url: productData.imageUrl,
        altText: `${productData.title} - Main Image`,
        isDefault: images.length === 0
      });
    }
    
    if (images.length === 0) {
      images.push({
        url: "https://via.placeholder.com/300x300?text=No+Image",
        altText: "No image available",
        isDefault: true
      });
    }
    
    return images;
  };

  const navigateToProduct = (wishlistItem: WishlistItemDto) => {
    const product = wishlistItem.product;
    const variantInfo = wishlistItem.variantInfo;
    const productImages = getProductImages(product);
    const currentPrice = variantInfo?.price ?? product.price ?? 0;
    const comparePrice = product.compareAtPrice || 0;
    
    const transformedVariants = product.variants ? product.variants.map((variant: any) => ({
      attributes: {},
      sku: variant.sku || product.sku || 'N/A',
      price: variant.price || product.price || 0,
      quantity: variant.quantity || 0,
      weight: variant.weight || product.weight || 0,
      weightUnit: variant.weightUnit || product.weightUnit || 'g',
      requiresShipping: variant.requiresShipping ?? product.requiresShipping ?? false,
      taxable: variant.taxable ?? product.taxable ?? false,
      trackInventory: variant.trackInventory ?? product.trackInventory ?? false,
      isDefault: variant.isDefault || false
    })) : [];

    if (variantInfo && !transformedVariants.find(v => v.sku === variantInfo.sku)) {
      transformedVariants.push({
        attributes: {},
        sku: variantInfo.sku || 'N/A',
        price: variantInfo.price || 0,
        quantity: variantInfo.quantity || 0,
        weight: variantInfo.weight || 0,
        weightUnit: variantInfo.weightUnit || 'g',
        requiresShipping: variantInfo.requiresShipping ?? false,
        taxable: variantInfo.taxable ?? false,
        trackInventory: variantInfo.trackInventory ?? false,
        isDefault: variantInfo.isDefault || false
      });
    }

    // Specifications for product details (unused in current implementation)
    // const specifications = [
    //   {
    //     key: 'SKU',
    //     value: variantInfo?.sku || product.sku || 'N/A'
    //   },
    //   {
    //     key: 'Weight',
    //     value: variantInfo?.weight ? `${variantInfo.weight} ${variantInfo.weightUnit || 'g'}` :
    //       product.weight ? `${product.weight} ${product.weightUnit || 'g'}` : 'N/A'
    //   },
    //   {
    //     key: 'Category',
    //     value: product.categories?.[0] || 'N/A'
    //   },
    //   {
    //     key: 'Current Stock',
    //     value: (variantInfo?.quantity || product.quantity || 0).toString()
    //   },
    //   {
    //     key: 'Shipping',
    //     value: (variantInfo?.requiresShipping || product.requiresShipping) ? 'Yes' : 'No'
    //   }
    // ];

    const transformedProductData: ProductDto = {
      id: product.id || product._id || '',
      _id: product.id || product._id || '',
      title: product.title || '',
      slug: product.slug || product.id || '',
      description: product.description || '',
      sku: variantInfo?.sku || product.sku || '',
      price: currentPrice,
      compareAtPrice: comparePrice > 0 ? comparePrice : null,
      quantity: variantInfo?.quantity ?? product.quantity ?? 0,
      weight: variantInfo?.weight ?? product.weight ?? 0,
      weightUnit: variantInfo?.weightUnit ?? product.weightUnit ?? 'g',
      reviewCount: product.reviewCount || 0,
      ratingsQuantity: product.ratingsQuantity || 0,
      __v: product.__v || 0,
      images: productImages,
      categories: product.categories || [],
      variants: transformedVariants,
      isActive: product.isActive ?? true,
      isFeatured: product.isFeatured ?? false,
      hasVariants: product.hasVariants ?? (transformedVariants.length > 0),
      requiresShipping: variantInfo?.requiresShipping ?? product.requiresShipping ?? false,
      taxable: variantInfo?.taxable ?? product.taxable ?? false,
      trackInventory: variantInfo?.trackInventory ?? product.trackInventory ?? false,
      inventoryPolicy: product.inventoryPolicy === 'continue' ? 'continue' : 'deny',
      meta: { 
        views: product.meta?.views || 0,
        purchases: product.meta?.purchases || 0
      },   
      rating: product.rating ?? 0,
      tags: product.tags || [],
      createdAt: product.createdAt || new Date().toISOString(),
      updatedAt: product.updatedAt || new Date().toISOString(),
    };

    // Use the product ID directly
    const productId = product.id || product._id || 'product';
    const isVariantProduct = wishlistItem.variantSku && wishlistItem.variantSku !== product.id;
    navigate(`/product/${productId}${isVariantProduct ? `?variant=${encodeURIComponent(wishlistItem.variantSku || '')}` : ''}`, {
      state: { 
        productData: transformedProductData,
        source: 'wishlist',
        selectedVariantSku: wishlistItem.variantSku,
        variantInfo: variantInfo,
        originalWishlistData: wishlistItem,
        fromWishlist: true,
        wishlistItemId: wishlistItem._id || wishlistItem.id
      }
    });
  };

  const value: WishlistContextType = {
    items,
    addItem,
    removeItem,
    isItemInWishlist,
    clearWishlist,
    navigateToProduct,
    loading,
    refreshWishlist
  };

  return (
    <WishlistContext.Provider value={value}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const context = useContext(WishlistContext);
  if (context === undefined) {
    throw new Error('useWishlist must be used within a WishlistProvider');
  }
  return context;
}
